// src/pages/admin/components/GroupManagement/GroupAssignments.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../../../firebase';

const formatDateForInput = (timestamp) => {
  if (!timestamp) return '';

  try {
    const date = (timestamp && typeof timestamp.toDate === 'function')
      ? timestamp.toDate()
      : (timestamp instanceof Date)
        ? timestamp
        : new Date(timestamp);

    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    console.error("Date formatting error:", e);
    return '';
  }
};

const formatDateForDisplay = (timestamp) => {
  if (!timestamp) return 'Not set';

  try {
    const date = (timestamp && typeof timestamp.toDate === 'function')
      ? timestamp.toDate()
      : (timestamp instanceof Date)
        ? timestamp
        : new Date(timestamp);

    if (isNaN(date.getTime())) return 'Invalid date';

    return date.toLocaleDateString();
  } catch (e) {
    console.error("Date display error:", e);
    return 'Invalid date';
  }
};

const GroupAssignments = ({ group, isEditing }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [assignmentSettings, setAssignmentSettings] = useState({});
  const [pendingChanges, setPendingChanges] = useState({});
  const [saveStatus, setSaveStatus] = useState("idle");

  useEffect(() => {
    const fetchAssignments = async () => {
      if (!group) return;

      try {
        setLoading(true);
        setError(null);

        const assignmentsSnapshot = await getDocs(collection(db, "assignments"));
        const assignmentsData = [];

        assignmentsSnapshot.forEach(doc => {
          const data = doc.data();

          assignmentsData.push({
            id: doc.id,
            title: data.title || "Untitled Assignment",
            instruction: data.instruction || "",
            startDate: data.startDate,
            endDate: data.endDate,
            order: data.order || 0,
            targetGroups: data.targetGroups || [],
            groupSettings: data.groupSettings || {},
            createdAt: data.createdAt
          });
        });

        assignmentsData.sort((a, b) => {
          const orderA = typeof a.order === 'number' ? a.order : 0;
          const orderB = typeof b.order === 'number' ? b.order : 0;
          return orderA - orderB;
        });

        const initialSettings = {};
        assignmentsData.forEach(assignment => {
          const groupSetting = (assignment.groupSettings && assignment.groupSettings[group.id]) || {};

          initialSettings[assignment.id] = {
            visible: (assignment.targetGroups.includes(group.id)) || (!assignment.targetGroups.length),
            startDate: groupSetting.startDate || assignment.startDate,
            endDate: groupSetting.endDate || assignment.endDate,
          };
        });

        setAssignments(assignmentsData);
        setAssignmentSettings(initialSettings);
        setPendingChanges({});
        setSaveStatus("idle");
      } catch (error) {
        console.error("Error loading assignments:", error);
        setError("Failed to load assignments: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [group]);

  useEffect(() => {
    setPendingChanges({});
    setSaveStatus("idle");
  }, [group?.id]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const updateGroupAssignmentSettings = async (assignmentId, newSettings) => {
    if (!group) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const assignmentRef = doc(db, "assignments", assignmentId);
      const assignmentDoc = assignments.find(a => a.id === assignmentId);

      if (!assignmentDoc) throw new Error("Assignment not found");

      const toTimestamp = (value) => {
        if (!value) return null;
        if (typeof value.toDate === 'function') return value;
        if (value instanceof Date) return Timestamp.fromDate(value);
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
      };

      const startDateTimestamp = toTimestamp(newSettings.startDate);
      const endDateTimestamp = toTimestamp(newSettings.endDate);

      const updateData = {
        groupSettings: {
          ...assignmentDoc.groupSettings,
          [group.id]: {
            startDate: startDateTimestamp,
            endDate: endDateTimestamp,
            updatedAt: serverTimestamp()
          }
        }
      };

      if (newSettings.visible) {
        if (!assignmentDoc.targetGroups.includes(group.id)) {
          await updateDoc(assignmentRef, { targetGroups: arrayUnion(group.id), ...updateData });
        } else {
          await updateDoc(assignmentRef, updateData);
        }
      } else {
        if (assignmentDoc.targetGroups.includes(group.id)) {
          await updateDoc(assignmentRef, { targetGroups: arrayRemove(group.id), ...updateData });
        } else {
          await updateDoc(assignmentRef, updateData);
        }
      }

      setAssignmentSettings({
        ...assignmentSettings,
        [assignmentId]: {
          ...newSettings,
          startDate: startDateTimestamp,
          endDate: endDateTimestamp
        }
      });

      setPendingChanges(prev => {
        const updated = { ...prev };
        delete updated[assignmentId];
        return updated;
      });

      const updatedAssignments = [...assignments];
      const index = updatedAssignments.findIndex(a => a.id === assignmentId);

      if (index !== -1) {
        updatedAssignments[index] = {
          ...updatedAssignments[index],
          targetGroups: newSettings.visible
            ? [...new Set([...updatedAssignments[index].targetGroups, group.id])]
            : updatedAssignments[index].targetGroups.filter(id => id !== group.id),
          groupSettings: {
            ...updatedAssignments[index].groupSettings,
            [group.id]: {
              startDate: startDateTimestamp,
              endDate: endDateTimestamp,
              updatedAt: serverTimestamp()
            }
          }
        };
        setAssignments(updatedAssignments);
      }

      setSuccess("Assignment settings updated for this group");

      if (Object.keys(pendingChanges).length === 0) {
        setSaveStatus("saved");
      }
    } catch (error) {
      console.error("Error updating assignment settings:", error);
      setError("Failed to update assignment settings: " + error.message);
      setSaveStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (assignmentId, field, dateString) => {
    const currentSettings = assignmentSettings[assignmentId];
    if (!currentSettings) return;

    let newDate = null;
    if (dateString && dateString.trim() !== '') {
      try {
        newDate = new Date(dateString);
        if (isNaN(newDate.getTime())) return;
      } catch (e) {
        console.error("Error parsing date:", e);
        return;
      }
    }

    const toDate = (val) => {
      if (!val) return null;
      if (typeof val.toDate === 'function') return val.toDate();
      if (val instanceof Date) return val;
      return new Date(val);
    };

    const currentStartDate = toDate(currentSettings.startDate);
    const currentEndDate = toDate(currentSettings.endDate);

    if (field === 'startDate' && newDate && currentEndDate && newDate > currentEndDate) {
      alert('Start date cannot be after end date');
      return;
    }
    if (field === 'endDate' && newDate && currentStartDate && newDate < currentStartDate) {
      alert('End date cannot be before start date');
      return;
    }

    setPendingChanges(prev => ({
      ...prev,
      [assignmentId]: { ...(prev[assignmentId] || {}), [field]: newDate }
    }));

    setAssignmentSettings(prev => ({
      ...prev,
      [assignmentId]: { ...prev[assignmentId], [field]: newDate }
    }));

    if (saveStatus === "saved") setSaveStatus("idle");
  };

  const handleToggleVisibility = (assignmentId) => {
    const currentSettings = assignmentSettings[assignmentId];
    if (!currentSettings) return;

    setPendingChanges(prev => ({
      ...prev,
      [assignmentId]: { ...(prev[assignmentId] || {}), visible: !currentSettings.visible }
    }));

    setAssignmentSettings(prev => ({
      ...prev,
      [assignmentId]: { ...prev[assignmentId], visible: !currentSettings.visible }
    }));

    if (saveStatus === "saved") setSaveStatus("idle");
  };

  const saveAllChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) return;

    try {
      setSaveStatus("saving");

      for (const assignmentId of Object.keys(pendingChanges)) {
        const currentSettings = assignmentSettings[assignmentId];
        const changedFields = pendingChanges[assignmentId];
        if (!currentSettings) continue;

        await updateGroupAssignmentSettings(assignmentId, {
          ...currentSettings,
          ...changedFields
        });
      }

      setPendingChanges({});
      setSaveStatus("saved");
      setSuccess("All assignment settings updated for this group");
    } catch (error) {
      console.error("Error saving all changes:", error);
      setError("Failed to save changes: " + error.message);
      setSaveStatus("error");
    }
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  if (loading && !assignments.length) {
    return <div className="loading-message">Loading assignments...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!group) {
    return <div className="no-group-selected">Please select a group to manage assignments</div>;
  }

  return (
    <div className="group-assignments-container">
      <div className="group-assignments-header">
        <h4>Assignments for this Group</h4>

        {isEditing && (
          <button
            className={`save-changes-btn ${saveStatus} ${hasPendingChanges ? 'has-changes' : ''}`}
            onClick={saveAllChanges}
            disabled={saveStatus === "saving" || !hasPendingChanges}
          >
            {saveStatus === "saving" && "SAVING..."}
            {saveStatus === "error" && "ERROR - TRY AGAIN"}
            {(saveStatus !== "saving" && saveStatus !== "error") && (
              hasPendingChanges ? "SAVE CHANGES" : "SAVED"
            )}
          </button>
        )}
      </div>

      {success && <div className="success-message">{success}</div>}

      {assignments.length === 0 ? (
        <div className="no-assignments">
          <p>No assignments are available.</p>
        </div>
      ) : (
        <div className="assignments-list">
          <table className="assignments-table">
            <thead>
              <tr>
                <th>Assignment</th>
                <th>Visibility</th>
                <th>Start Date</th>
                <th>End Date</th>
                {isEditing && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {[...assignments].sort((a, b) => (a.order || 0) - (b.order || 0)).map(assignment => {
                const baseSettings = assignmentSettings[assignment.id] || {
                  visible: assignment.targetGroups.includes(group.id) || !assignment.targetGroups.length,
                  startDate: assignment.groupSettings?.[group.id]?.startDate || assignment.startDate,
                  endDate: assignment.groupSettings?.[group.id]?.endDate || assignment.endDate
                };

                const pendingSettingsForThis = pendingChanges[assignment.id] || {};
                const displaySettings = { ...baseSettings, ...pendingSettingsForThis };
                const hasChanges = !!pendingChanges[assignment.id];

                return (
                  <tr key={assignment.id} className={hasChanges ? 'has-pending-changes' : ''}>
                    <td>
                      <div className="assignment-title">{assignment.title}</div>
                    </td>
                    <td>
                      {isEditing ? (
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={displaySettings.visible}
                            onChange={() => handleToggleVisibility(assignment.id)}
                          />
                          <span className="slider"></span>
                          {hasChanges && pendingChanges[assignment.id].visible !== undefined &&
                            <span className="change-indicator">*</span>}
                        </label>
                      ) : (
                        <span>{displaySettings.visible ? 'Visible' : 'Hidden'}</span>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <div className="date-input-container">
                          <input
                            type="date"
                            value={formatDateForInput(displaySettings.startDate)}
                            onChange={(e) => handleDateChange(assignment.id, 'startDate', e.target.value)}
                            disabled={!displaySettings.visible}
                          />
                          {hasChanges && pendingChanges[assignment.id].startDate !== undefined &&
                            <span className="change-indicator">*</span>}
                        </div>
                      ) : (
                        formatDateForDisplay(displaySettings.startDate)
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <div className="date-input-container">
                          <input
                            type="date"
                            value={formatDateForInput(displaySettings.endDate)}
                            onChange={(e) => handleDateChange(assignment.id, 'endDate', e.target.value)}
                            disabled={!displaySettings.visible}
                          />
                          {hasChanges && pendingChanges[assignment.id].endDate !== undefined &&
                            <span className="change-indicator">*</span>}
                        </div>
                      ) : (
                        formatDateForDisplay(displaySettings.endDate)
                      )}
                    </td>
                    {isEditing && (
                      <td>
                        {hasChanges && (
                          <button
                            className="save-row-btn"
                            onClick={async () => {
                              const currentSettings = assignmentSettings[assignment.id];
                              const changedFields = pendingChanges[assignment.id];
                              if (!currentSettings) return;
                              await updateGroupAssignmentSettings(assignment.id, {
                                ...currentSettings,
                                ...changedFields
                              });
                            }}
                          >
                            Save
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isEditing && hasPendingChanges && (
        <div className="floating-save-container">
          <button
            className={`floating-save-btn ${saveStatus}`}
            onClick={saveAllChanges}
            disabled={saveStatus === "saving"}
          >
            {saveStatus === "saving"
              ? "SAVING..."
              : `SAVE ALL CHANGES (${Object.keys(pendingChanges).length})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupAssignments;